import { useLocalStorageState } from '@side/pantry';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { paymentApi } from '$api-clients/clients';
import {
  type ChatMessage,
  getInteractionStorageKey,
  getMessageId,
  INITIAL_MESSAGE,
  INTRO_MESSAGE_ID,
  isHistoryMessage,
  type PendingRequestType,
  type ReportingInsightsChatHistoryQuery,
  type ReportingInsightsChatQuery,
  type ReportingInsightsChatRequestBody,
  type ReportingInsightsPromptId,
  resolveStoredInteractionId,
  type StoredInteractionContext,
} from '../components/AIInsightsButton.helpers';

export const useAIInsightsChat = ({
  isOpen,
  viewReportAs,
}: {
  isOpen: boolean;
  viewReportAs: string;
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoringHistory, setIsRestoringHistory] = useState(false);
  const [pendingRequestType, setPendingRequestType] =
    useState<PendingRequestType>(null);
  const [interactionId, setInteractionId] = useState<string | null>(null);
  const restoredInteractionIdRef = useRef<string | null>(null);
  const activeViewReportAsRef = useRef(viewReportAs);
  const chatQuery = useMemo<ReportingInsightsChatQuery>(
    () => ({
      viewReportAs,
    }),
    [viewReportAs],
  );
  const interactionStorageKey = useMemo(
    () => getInteractionStorageKey(viewReportAs),
    [viewReportAs],
  );
  const [, setStoredInteractionContext] =
    useLocalStorageState<StoredInteractionContext>(interactionStorageKey, null);

  const setAndPersistInteractionId = useCallback(
    (nextInteractionId: string | null) => {
      setInteractionId(nextInteractionId);
      restoredInteractionIdRef.current = nextInteractionId;

      if (!nextInteractionId) {
        setStoredInteractionContext(null);
        return;
      }

      setStoredInteractionContext({
        interactionId: nextInteractionId,
      });
    },
    [setStoredInteractionContext],
  );

  useEffect(() => {
    if (activeViewReportAsRef.current === viewReportAs) {
      return;
    }

    activeViewReportAsRef.current = viewReportAs;
    restoredInteractionIdRef.current = null;
    setInteractionId(null);
    setMessages([INITIAL_MESSAGE]);
    setInputMessage('');
    setPendingRequestType(null);
  }, [viewReportAs]);

  useEffect(() => {
    if (!isOpen || isLoading) {
      return;
    }

    const rawStoredInteractionContext = window.localStorage.getItem(
      interactionStorageKey,
    );
    let parsedStoredInteractionContext: StoredInteractionContext = null;

    if (rawStoredInteractionContext) {
      try {
        parsedStoredInteractionContext = JSON.parse(
          rawStoredInteractionContext,
        ) as StoredInteractionContext;
      } catch {
        parsedStoredInteractionContext = rawStoredInteractionContext;
      }
    }

    const storedInteractionId = resolveStoredInteractionId(
      parsedStoredInteractionContext,
    );

    if (!storedInteractionId) {
      return;
    }

    if (restoredInteractionIdRef.current === storedInteractionId) {
      return;
    }

    let isCancelled = false;
    setIsRestoringHistory(true);

    const restoreHistory = async () => {
      try {
        const historyQuery: ReportingInsightsChatHistoryQuery = {
          viewReportAs: chatQuery.viewReportAs,
          interactionId: storedInteractionId,
        };

        const { data, error } = await paymentApi.GET(
          '/reporting/insights/chat/history',
          {
            params: {
              query: historyQuery,
            },
          },
        );

        if (isCancelled) {
          return;
        }

        if (
          error !== undefined ||
          !data.interactionId ||
          !Array.isArray(data.history)
        ) {
          throw error ?? new Error('No chat history returned');
        }

        const restoredMessages: ChatMessage[] = data.history
          .filter(isHistoryMessage)
          .map((historyMessage) => ({
            id: getMessageId(),
            role: historyMessage.role,
            content:
              historyMessage.displayContent?.trim() ||
              historyMessage.content.trim(),
          }));

        setMessages([INITIAL_MESSAGE, ...restoredMessages]);
        setAndPersistInteractionId(data.interactionId);
      } catch {
        if (isCancelled) {
          return;
        }

        setStoredInteractionContext(null);
        restoredInteractionIdRef.current = null;
        setInteractionId(null);
      } finally {
        if (!isCancelled) {
          setIsRestoringHistory(false);
        }
      }
    };

    void restoreHistory();

    return () => {
      isCancelled = true;
    };
  }, [
    chatQuery,
    interactionStorageKey,
    isLoading,
    isOpen,
    setAndPersistInteractionId,
    setStoredInteractionContext,
  ]);

  const appendMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const sendChatRequest = useCallback(
    async ({
      body,
      pendingType,
      errorMessage,
    }: {
      body: ReportingInsightsChatRequestBody;
      pendingType: Exclude<PendingRequestType, null>;
      errorMessage: string;
    }) => {
      setPendingRequestType(pendingType);
      setIsLoading(true);

      try {
        const { data, error } = await paymentApi.POST(
          '/reporting/insights/chat',
          {
            params: { query: chatQuery },
            body,
          },
        );

        if (error !== undefined || !data.message) {
          throw error ?? new Error('No message returned');
        }

        setAndPersistInteractionId(data.interactionId ?? interactionId ?? null);
        appendMessage({
          id: getMessageId(),
          role: 'assistant',
          content: data.message,
        });
      } catch {
        appendMessage({
          id: getMessageId(),
          role: 'assistant',
          isError: true,
          content: errorMessage,
        });
      } finally {
        setIsLoading(false);
        setPendingRequestType(null);
      }
    },
    [appendMessage, chatQuery, interactionId, setAndPersistInteractionId],
  );

  const clearChat = useCallback(() => {
    setMessages([INITIAL_MESSAGE]);
    setInputMessage('');
    setPendingRequestType(null);
    setAndPersistInteractionId(null);
  }, [setAndPersistInteractionId]);

  const runPrompt = useCallback(
    async (promptId: ReportingInsightsPromptId, label: string) => {
      if (isLoading || isRestoringHistory) {
        return;
      }

      appendMessage({
        id: getMessageId(),
        role: 'user',
        content: label,
      });

      await sendChatRequest({
        body: {
          promptId,
          interactionId: interactionId ?? undefined,
        },
        pendingType: 'prompt',
        errorMessage:
          'Unable to generate insights right now. Please try again.',
      });
    },
    [
      appendMessage,
      interactionId,
      isLoading,
      isRestoringHistory,
      sendChatRequest,
    ],
  );

  const sendFollowUp = useCallback(async () => {
    if (isLoading || isRestoringHistory || !inputMessage.trim()) {
      return;
    }

    const message = inputMessage.trim();

    appendMessage({
      id: getMessageId(),
      role: 'user',
      content: message,
    });
    setInputMessage('');

    await sendChatRequest({
      body: {
        message,
        interactionId: interactionId ?? undefined,
      },
      pendingType: 'follow_up',
      errorMessage:
        'Unable to generate a follow-up response. Please try again.',
    });
  }, [
    appendMessage,
    inputMessage,
    interactionId,
    isLoading,
    isRestoringHistory,
    sendChatRequest,
  ]);

  const hasAssistantInsight = useMemo(
    () =>
      messages.some(
        (message) =>
          message.role === 'assistant' && message.id !== INTRO_MESSAGE_ID,
      ),
    [messages],
  );
  const isBusy = isLoading || isRestoringHistory;
  const canSendMessage = !isBusy && inputMessage.trim().length > 0;
  const loadingMessage = useMemo(() => {
    if (isRestoringHistory) {
      return 'Restoring your previous chat...';
    }

    if (pendingRequestType === 'follow_up') {
      return 'Working on your follow-up... This may take a minute...';
    }

    return 'Analyzing your report data... This may take a minute...';
  }, [isRestoringHistory, pendingRequestType]);

  return {
    messages,
    interactionId,
    inputMessage,
    setInputMessage,
    isBusy,
    isLoading,
    isRestoringHistory,
    canSendMessage,
    loadingMessage,
    hasAssistantInsight,
    runPrompt,
    sendFollowUp,
    clearChat,
  };
};
